import type { ItemPickerInstance } from '@miso/design-system';
import type {
  AdditionalSpaceObservingSession,
  SketchingSessionRoom,
  VisitCounselView,
} from '@/entities/visit-counsel/model/visit-counsel.types';
import { formatSketchingRoomLabelSpaced } from '@/entities/visit-counsel/model/sketchRoomDisplayLabel';
import {
  isSingletonRoomType,
  recoverRoomType,
} from '@/entities/visit-counsel/model/recoverRoomType';
import { isFormalAdditionalSpaceType } from '@/entities/visit-counsel/model/formalAdditionalSpace';
import { sortAdditionalSpaceObservingSessions } from '@/features/visit-counsel/counsel-progress/model/additionalSpaceObservingOrder';
import { SPACE_TYPE_LABELS } from '@/features/visit-counsel/counsel-progress/model/counsel-progress.constants';

/**
 * 추가공간 picker (`AdditionalSpaceFlowAdapter`) 의 카드 1개를 표현하는 entry.
 *
 * `RoomObservingFlowAdapter` 의 `ObservingPickerEntry` 를 본 stage 의 범위(추가공간만)에
 * 맞춰 단순화한 형태다. sketch entry 가 없고 항상 `AdditionalSpaceObservingSession`
 * 기반이라 `kind` discriminator 가 불필요.
 *
 * - `linkedSketchRoom` 있음 — sketching 단계에서 "+ 공간 추가" 로 등록된 공식 추가공간
 *   (BALCONY/RESIDENCE_STORAGE) 의 옵저빙 세션. 라벨/이모지는 sketching 룸 메타를 따른다.
 * - `linkedSketchRoom` 없음 — sketching 룸과 매칭되지 않는 추가공간 세션. 보통 사용자가
 *   숨겨둔 운반 경로(`LADDER_TRUCK_ENTRANCE` 등) 가 pre-progress 에서 처리되지 못하고
 *   넘어온 edge-case. label/icon 은 `SPACE_TYPE_LABELS` / spaceType-default 이모지.
 */
export interface AdditionalSpacePickerEntry {
  /** picker 카드 id — sketching roomId 가 안정적이라 우선, 없으면 `additional:${sessionId}` */
  itemId: string;
  title: string;
  description?: string;
  iconEmoji: string;
  done: boolean;
  hasItems: boolean;
  hasVideo: boolean;
  sessionId: string;
  session: AdditionalSpaceObservingSession;
  linkedSketchRoom?: SketchingSessionRoom;
  /** `MovingItemsFlowAdapter.isFirstStart` 로 그대로 전달 */
  isFirstStart: boolean;
  /** `MovingItemsFlowAdapter.canRecordMovingItems` 로 그대로 전달 */
  canRecordMovingItems: boolean;
}

// ── 아이콘 매핑 (room-observing-flow 의 매핑과 동일 — 단일 진실 소스로 통합은 follow-up) ─

const ROOM_ICON_BY_TYPE: Record<string, string> = {
  LIVING_ROOM: '🛋️',
  KITCHEN: '🍳',
  ROOM: '🛌',
  BATH_ROOM: '🚿',
  BALCONY: '🪟',
  RESIDENCE_STORAGE: '📦',
  OTHER_SPACE: '📦',
};

const ADDITIONAL_SPACE_ICON_BY_TYPE: Record<string, string> = {
  LADDER_TRUCK_ENTRANCE: '🚚',
  FIRST_FLOOR_TRUCK_ENTRANCE: '🛻',
  STAIRS: '🪜',
  RESIDENCE_STORAGE: '📦',
  BATH_ROOM: '🚿',
  BALCONY: '🪟',
  OTHER_SPACE: '📦',
};

// ── doneness 계산 (`buildObservingPickerSections.additionalSessionDoneness` 와 정렬) ──

function additionalSessionDoneness(session: AdditionalSpaceObservingSession): {
  hasVideo: boolean;
  hasItems: boolean;
  done: boolean;
} {
  const hasVideo = !!session?.video;
  const hasItems =
    session.canRecordMovingItems !== true
      ? false
      : (session.movingItems?.length ?? 0) > 0 || (session.movingItemSpecIds?.length ?? 0) > 0;
  const done =
    session.status === 'COMPLETED' ||
    (session.canRecordMovingItems !== true ? hasVideo : hasVideo && hasItems);
  return { hasVideo, hasItems, done };
}

// ── entry 빌더 ─────────────────────────────────────────────────────────────────

function makeLinkedEntry(
  session: AdditionalSpaceObservingSession,
  room: SketchingSessionRoom,
  rooms: SketchingSessionRoom[]
): AdditionalSpacePickerEntry {
  const { hasVideo, hasItems, done } = additionalSessionDoneness(session);
  const effectiveType = recoverRoomType(room);
  const singleton = isSingletonRoomType(room, rooms);
  return {
    itemId: room.roomId ?? `additional:${session.id}`,
    title: formatSketchingRoomLabelSpaced(room, singleton ? { singleton: true } : undefined),
    iconEmoji:
      ROOM_ICON_BY_TYPE[effectiveType] ?? ADDITIONAL_SPACE_ICON_BY_TYPE[session.spaceType] ?? '📦',
    done,
    hasItems,
    hasVideo,
    sessionId: session.id,
    session,
    linkedSketchRoom: room,
    isFirstStart: session.status === 'REQUESTED',
    canRecordMovingItems: session.canRecordMovingItems === true,
  };
}

function makeUnlinkedEntry(session: AdditionalSpaceObservingSession): AdditionalSpacePickerEntry {
  const { hasVideo, hasItems, done } = additionalSessionDoneness(session);
  return {
    itemId: `additional:${session.id}`,
    title: SPACE_TYPE_LABELS[session.spaceType] ?? session.spaceType,
    iconEmoji: ADDITIONAL_SPACE_ICON_BY_TYPE[session.spaceType] ?? '📦',
    done,
    hasItems,
    hasVideo,
    sessionId: session.id,
    session,
    isFirstStart: session.status === 'REQUESTED',
    canRecordMovingItems: session.canRecordMovingItems === true,
  };
}

// ── 메인 빌더 ──────────────────────────────────────────────────────────────────

/**
 * `additional-spaces` stage 의 picker 에 노출할 entry 배열을 만든다.
 *
 * 정책 (`formalAdditionalSpace.ts` 정렬):
 *
 *   1. **공식 추가공간 (`FORMAL_ADDITIONAL_SPACE_TYPES`)** — 항상 노출 (완료/미완료 모두).
 *      sketching 룸과 같은 `roomId` 가 있으면 linked 로 합쳐 sketching 라벨/이모지를 사용,
 *      없으면 spaceType 라벨/이모지로 단독 노출. 사용자가 progress 7-stage 뒤에 묶어
 *      촬영하는 보조 공간 — 완료 항목에도 done badge 가 보이게 한다.
 *   2. **그 외(예: 운반 경로) 추가공간** — 정상 흐름에서는 pre-progress 단계에서 처리되어
 *      여기에 도달하지 않는다. 그러나 만약 이 단계까지 흘러 들어오면 완료/미완료 모두
 *      picker 에 포함해 done badge 와 영상 재촬영이 가능하도록 한다.
 *
 * 정렬은 `sortAdditionalSpaceObservingSessions` 의 기존 정책을 그대로 따른다.
 */
export function buildAdditionalSpacePickerEntries(
  counsel: VisitCounselView | null | undefined
): AdditionalSpacePickerEntry[] {
  if (!counsel) return [];
  const additional = sortAdditionalSpaceObservingSessions(
    counsel.sessions?.additionalSpaceObserving ?? []
  );
  if (additional.length === 0) return [];

  const sketchRooms = counsel.sessions?.sketching?.rooms ?? [];
  const sketchRoomById = new Map<string, SketchingSessionRoom>();
  for (const r of sketchRooms) {
    if (r.roomId) sketchRoomById.set(r.roomId, r);
  }

  const entries: AdditionalSpacePickerEntry[] = [];
  for (const session of additional) {
    const linkedRoom = session.roomId ? sketchRoomById.get(session.roomId) : undefined;

    // OTHER_SPACE 룸(사용자 자유 입력 공간)과 연결된 세션은 pre-progress 에서 처리된다.
    // recoverRoomType 이 "발코니" → BALCONY 로 복원하더라도 이 단계에서는 제외.
    if (linkedRoom?.type === 'OTHER_SPACE') continue;

    // isAdditionalSpace === false 이면 type 이 BALCONY 여도 비공식으로 취급한다.
    const formal =
      session.isAdditionalSpace !== false && isFormalAdditionalSpaceType(session.spaceType);
    // sketching 룸이 같은 roomId 로 있으면 type 도 검사해 fallback 안정성 확보.
    // isAdditionalSpace === false 인 방은 type 이 BALCONY 여도 비공식 처리.
    const linkedIsFormal = linkedRoom
      ? linkedRoom.isAdditionalSpace !== false &&
        isFormalAdditionalSpaceType(recoverRoomType(linkedRoom))
      : false;

    if (formal || linkedIsFormal) {
      // 공식 추가공간 — 완료/미완료 모두 노출.
      if (linkedRoom) {
        entries.push(makeLinkedEntry(session, linkedRoom, sketchRooms));
      } else {
        entries.push(makeUnlinkedEntry(session));
      }
      continue;
    }

    // 비공식 (예: 운반 경로) — 완료/미완료 모두 노출.
    // 완료 후에도 picker 에 남아 done badge 가 표시되고 영상 재촬영이 가능해야 한다.
    if (linkedRoom) {
      entries.push(makeLinkedEntry(session, linkedRoom, sketchRooms));
    } else {
      entries.push(makeUnlinkedEntry(session));
    }
  }
  return entries;
}

// ── prefill helper (방 어댑터의 `entriesToDoneInstances` 와 동일 패턴) ───────────

/**
 * 완료된 entry → `ItemPickerInstance` prefill. navigation 모드의 ItemPicker 는
 * `instances.length > 0` 인 카드를 자동으로 done badge 렌더한다.
 */
export function additionalSpaceEntriesToDoneInstances(
  entries: AdditionalSpacePickerEntry[]
): ItemPickerInstance[] {
  const out: ItemPickerInstance[] = [];
  for (const e of entries) {
    if (!e.done) continue;
    out.push({
      instanceId: `done_${e.itemId}`,
      itemId: e.itemId,
      answers: {},
    });
  }
  return out;
}

/**
 * 모든 entry 가 완료되었는지 — 어댑터의 onAfterDone 결정에 사용.
 */
export function allAdditionalSpaceEntriesDone(entries: AdditionalSpacePickerEntry[]): boolean {
  if (entries.length === 0) return true;
  return entries.every((e) => e.done);
}
